import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { adminUserApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
    Plus, KeyRound, Shield, Power, Loader2, Mail, ShieldOff, AlertTriangle,
    UserPlus, Pencil, CheckCircle2, XCircle, Crown, Lock, Unlock,
} from 'lucide-react';

const ROLE_LABEL = {
    super_admin: { label: 'Super-Admin', cls: 'bg-violet-100 text-violet-800', Icon: Crown },
    admin: { label: 'Admin', cls: 'bg-blue-100 text-blue-800', Icon: Shield },
};

export function UserManagementCard() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [pwUserId, setPwUserId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await adminUserApi.list();
            setUsers(r.data || []);
        } catch {
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const handleRole = async (u, role) => {
        if (role === u.role) return;
        try {
            await adminUserApi.update(u.id, { role });
            toast.success(`Rolle auf ${ROLE_LABEL[role]?.label || role} gesetzt`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    const handleToggleActive = async (u) => {
        const newActive = !u.is_active;
        if (!newActive && !window.confirm(`Konto ${u.email} deaktivieren? Aktive Sessions werden ausgeloggt.`)) return;
        try {
            await adminUserApi.update(u.id, { is_active: newActive });
            toast.success(newActive ? 'Konto aktiviert' : 'Konto deaktiviert');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    const handleResetLink = async (u) => {
        if (!window.confirm(`Passwort-Reset-Mail an ${u.email} senden?`)) return;
        try {
            await adminUserApi.sendResetLink(u.id);
            toast.success('Reset-Link versendet');
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    const handleResetTwoFA = async (u) => {
        if (!window.confirm(`2FA bei ${u.email} zurücksetzen? Nach dem nächsten Login kann der Nutzer 2FA neu einrichten.`)) return;
        try {
            await adminUserApi.resetTwoFA(u.id);
            toast.success('2FA zurückgesetzt');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    const handleUnlock = async (u) => {
        if (!window.confirm(`Konto ${u.email} entsperren? Brute-Force-Zähler wird zurückgesetzt.`)) return;
        try {
            const r = await adminUserApi.unlockAccount(u.id);
            toast.success(r.data?.was_locked ? 'Konto entsperrt' : 'Fehlversuch-Zähler zurückgesetzt');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    // Konto-Sperre erkennen: locked_until liegt in der Zukunft.
    const isLocked = (u) => {
        if (!u.locked_until) return false;
        try { return new Date(u.locked_until).getTime() > Date.now(); } catch { return false; }
    };
    const lockMinutesLeft = (u) => {
        if (!isLocked(u)) return 0;
        try {
            const diffMs = new Date(u.locked_until).getTime() - Date.now();
            return Math.max(1, Math.ceil(diffMs / 60000));
        } catch { return 0; }
    };

    return (
        <Card data-testid="user-management-card">
            <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Shield className="h-5 w-5" />
                            Benutzerverwaltung
                        </CardTitle>
                        <CardDescription>
                            Admin- und Super-Admin-Konten verwalten. Nur sichtbar für Super-Admins.
                        </CardDescription>
                    </div>
                    <Button onClick={() => setCreateOpen(true)} data-testid="create-user-btn">
                        <UserPlus className="h-4 w-4 mr-1.5" /> Neuer Admin
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Lade…</div>
                ) : users.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">Keine Konten gefunden.</p>
                ) : (
                    <ul className="flex flex-col divide-y border rounded-lg overflow-hidden">
                        {users.map(u => {
                            const role = ROLE_LABEL[u.role] || ROLE_LABEL.admin;
                            const Icon = role.Icon;
                            const isMe = u.id === currentUser?.id;
                            return (
                                <li key={u.id} className="px-3 py-3 flex flex-wrap items-center gap-3 bg-white" data-testid={`user-row-${u.id}`}>
                                    <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                        <span className="text-xs font-semibold text-gray-700">
                                            {(u.first_name?.[0] || '') + (u.last_name?.[0] || '')}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-[180px]">
                                        <div className="flex items-baseline gap-2 flex-wrap">
                                            <p className="font-medium text-sm">
                                                {u.first_name} {u.last_name}
                                                {isMe && <span className="ml-2 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Sie</span>}
                                            </p>
                                            <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${role.cls}`}>
                                                <Icon className="h-3 w-3" />{role.label}
                                            </span>
                                            {u.twofa_enabled && (
                                                <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                                                    <Shield className="h-3 w-3" />2FA
                                                </span>
                                            )}
                                            {!u.is_active && (
                                                <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700">Deaktiviert</span>
                                            )}
                                            {isLocked(u) && (
                                                <span
                                                    className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-800 inline-flex items-center gap-1"
                                                    title={`Konto gesperrt nach Fehlversuchen (noch ${lockMinutesLeft(u)} Min)`}
                                                    data-testid={`locked-badge-${u.id}`}
                                                >
                                                    <Lock className="h-3 w-3" />
                                                    Gesperrt ({lockMinutesLeft(u)} Min)
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground">{u.email}</p>
                                    </div>
                                    {/* Rolle */}
                                    <Select value={u.role} onValueChange={(v) => handleRole(u, v)} disabled={isMe}>
                                        <SelectTrigger className="w-36 h-8 text-xs" data-testid={`role-select-${u.id}`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="admin">Admin</SelectItem>
                                            <SelectItem value="super_admin">Super-Admin</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {/* Aktiv-Schalter */}
                                    <div className="flex items-center gap-1 text-xs" title="Aktiv">
                                        <Power className="h-3.5 w-3.5 text-muted-foreground" />
                                        <Switch checked={u.is_active !== false} onCheckedChange={() => handleToggleActive(u)} disabled={isMe} data-testid={`active-toggle-${u.id}`} />
                                    </div>
                                    {/* Aktionen */}
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="sm" onClick={() => setPwUserId(u.id)} title="Passwort setzen" className="h-8 w-8 p-0" data-testid={`setpw-${u.id}`}>
                                            <KeyRound className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => handleResetLink(u)} title="Reset-Link senden" className="h-8 w-8 p-0" data-testid={`resetlink-${u.id}`}>
                                            <Mail className="h-3.5 w-3.5" />
                                        </Button>
                                        {u.twofa_enabled && (
                                            <Button variant="ghost" size="sm" onClick={() => handleResetTwoFA(u)} title="2FA zurücksetzen" className="h-8 w-8 p-0 text-amber-700" data-testid={`reset2fa-${u.id}`}>
                                                <ShieldOff className="h-3.5 w-3.5" />
                                            </Button>
                                        )}
                                        {isLocked(u) && (
                                            <Button variant="ghost" size="sm" onClick={() => handleUnlock(u)} title="Konto entsperren" className="h-8 w-8 p-0 text-emerald-700" data-testid={`unlock-${u.id}`}>
                                                <Unlock className="h-3.5 w-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </CardContent>
            <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
            <SetPasswordDialog userId={pwUserId} onClose={() => setPwUserId(null)} />
        </Card>
    );
}

function CreateUserDialog({ open, onOpenChange, onCreated }) {
    const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', role: 'admin' });
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        if (form.password.length < 8) { toast.error('Passwort min. 8 Zeichen'); return; }
        setLoading(true);
        try {
            await adminUserApi.create(form);
            toast.success('Konto angelegt');
            onCreated?.();
            onOpenChange(false);
            setForm({ first_name: '', last_name: '', email: '', password: '', role: 'admin' });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler');
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Neuer Admin-Account</DialogTitle>
                    <DialogDescription>Anlegen eines neuen Verwaltungs-Kontos.</DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-3">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs">Vorname</Label>
                            <Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} autoComplete="off" data-testid="new-user-firstname" />
                        </div>
                        <div>
                            <Label className="text-xs">Nachname</Label>
                            <Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} autoComplete="off" data-testid="new-user-lastname" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">E-Mail</Label>
                        <Input type="text" inputMode="email" autoComplete="off" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="new-user-email" />
                    </div>
                    <div>
                        <Label className="text-xs">Initial-Passwort (min. 8 Zeichen)</Label>
                        <Input type="text" autoComplete="new-password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="new-user-pw" />
                    </div>
                    <div>
                        <Label className="text-xs">Rolle</Label>
                        <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                            <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="super_admin">Super-Admin</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex justify-end gap-2 mt-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
                        <Button onClick={submit} disabled={loading || !form.email || !form.password} data-testid="new-user-submit">
                            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            <UserPlus className="h-4 w-4 mr-1.5" /> Anlegen
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function SetPasswordDialog({ userId, onClose }) {
    const [pw, setPw] = useState('');
    const [forceChange, setForceChange] = useState(true);
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        if (pw.length < 8) { toast.error('Min. 8 Zeichen'); return; }
        setLoading(true);
        try {
            await adminUserApi.setPassword(userId, { new_password: pw, require_change_on_login: forceChange });
            toast.success('Passwort gesetzt – Nutzer wird beim nächsten Login zur Änderung aufgefordert');
            setPw(''); onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler');
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>Passwort setzen</DialogTitle>
                    <DialogDescription>Direktes Setzen für Notfälle (Mitarbeiterwechsel, Lockout).</DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-3">
                    <Input type="text" placeholder="Neues Passwort" value={pw} onChange={e => setPw(e.target.value)} data-testid="setpw-input" />
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <Switch checked={forceChange} onCheckedChange={setForceChange} />
                        <span>Beim nächsten Login zur Änderung zwingen</span>
                    </label>
                    <div className="p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800 flex gap-1.5 items-start">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <span>Alle aktiven Sessions des Nutzers werden ausgeloggt.</span>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                        <Button onClick={submit} disabled={loading || pw.length < 8} data-testid="setpw-submit">
                            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Setzen
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
